export const DEFAULT_WRAP_WIDTH = 68;

// Compute the best wrap width for a passage given the sub-questions that reference it.
// Collects all line numbers referenced, takes the maximum, and divides the passage char
// count by that number so the wrapped line count matches the exam paper's line numbering.
export function computePassageWrapWidth(passageText, subQuestions = []) {
  if (!passageText) return DEFAULT_WRAP_WIDTH;
  const plain = passageText.replace(/\n/g, " ").trim();
  if (plain.length === 0) return DEFAULT_WRAP_WIDTH;

  const lineNums = [];
  for (const sq of subQuestions) {
    const text = sq?.question_text || "";
    const rangeM = text.matchAll(/lines?\s+(\d+)\s*[–—-]\s*(\d+)/gi);
    for (const m of rangeM) {
      lineNums.push(parseInt(m[1], 10), parseInt(m[2], 10));
    }
    const singleM = text.matchAll(/\bline\s+(\d+)/gi);
    for (const m of singleM) lineNums.push(parseInt(m[1], 10));
  }

  const maxLine = lineNums.length ? Math.max(...lineNums) : 0;
  if (maxLine < 10) return DEFAULT_WRAP_WIDTH;

  // Add ~10% for indented/short lines (paragraph breaks take up line space)
  return Math.max(30, Math.ceil(plain.length / (maxLine * 1.1)));
}

// Wrap a flat passage text (no newlines) into lines of ~wrapWidth chars.
// Returns the original text unchanged when it already has real line breaks
// or {L} markers, so this is safe to call on any passage string.
export function wordWrapPassage(text, wrapWidth = DEFAULT_WRAP_WIDTH) {
  if (!text) return text;
  const meaningfulLineCount = text.split("\n").filter((l) => l.trim().length > 10).length;
  if (meaningfulLineCount > 2 || text.trim().length <= 200 || /\{L\d+\}/.test(text)) {
    return text;
  }
  const words = text.trim().replace(/\n/g, " ").split(/\s+/);
  const lines = [];
  let cur = "";
  for (const word of words) {
    if (!cur) {
      cur = word;
    } else if (cur.length + 1 + word.length <= wrapWidth) {
      cur += " " + word;
    } else {
      lines.push(cur);
      cur = word;
    }
  }
  if (cur) lines.push(cur);
  return lines.join("\n");
}

// Build a line-numbered passage from flat prose: flatten, wrap to wrapWidth, then anchor
// {L#} markers at line 1 and every `every`-th line (PDF cadence: {L1}, {L5}, {L10}…).
// Strips any pre-existing {L#} markers, &nbsp;, and HTML tags first so it is idempotent and
// safe to call on rich-text editor output. PassageWithLineNumbers increments the counter for
// the unmarked lines between anchors, so the rendered numbering is continuous.
export function insertLineNumberMarkers(text, wrapWidth = DEFAULT_WRAP_WIDTH, every = 5) {
  if (!text) return text;
  const plain = text
    .replace(/\{L\d+\}/g, "")
    .replace(/<[^>]*>/g, " ")
    .replace(/ /g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (plain.length === 0) return text;

  const words = plain.split(" ");
  const lines = [];
  let cur = "";
  for (const word of words) {
    if (!cur) {
      cur = word;
    } else if (cur.length + 1 + word.length <= wrapWidth) {
      cur += " " + word;
    } else {
      lines.push(cur);
      cur = word;
    }
  }
  if (cur) lines.push(cur);

  return lines
    .map((line, idx) => {
      const lineNum = idx + 1;
      if (lineNum === 1 || lineNum % every === 0) {
        return `{L${lineNum}} ${line}`;
      }
      return line;
    })
    .join("\n");
}

// Strip all {L#} line-number markers from a passage (admin "clear numbers" affordance).
export function stripLineNumberMarkers(text) {
  if (!text) return text;
  return text.replace(/\{L\d+\}\s?/g, "");
}

// Extract a contiguous block of lines from a passage string.
// Handles two modes:
//   {L#} markers present → use explicit line numbers from the markers
//   no markers           → fall back to word-wrap + sequential index
export function extractPassageLines(passageText, fromLine, toLine, wrapWidth = DEFAULT_WRAP_WIDTH) {
  if (!passageText || fromLine < 1 || toLine < fromLine) return null;

  const hasMarkers = /\{L\d+\}/.test(passageText);

  if (hasMarkers) {
    // Build a map: lineNumber → array of text segments that belong to it.
    // Lines without a marker inherit the previous marker's number + offset.
    const rawLines = passageText.split("\n");
    const markerRe = /\{L(\d+)\}/g;

    // First pass: collect all lines and their marker numbers
    const numbered = []; // { lineNum, text }
    let currentNum = null;

    for (const rawLine of rawLines) {
      const clean = rawLine.replace(/\{L\d+\}/g, "").trim();
      const markerMatch = rawLine.match(/\{L(\d+)\}/);
      if (markerMatch) {
        currentNum = parseInt(markerMatch[1], 10);
        if (clean.length > 0) numbered.push({ lineNum: currentNum, text: clean });
      } else if (clean.length > 0 && currentNum !== null) {
        // Line after a marker — increment line counter for each wrapped line
        currentNum += 1;
        numbered.push({ lineNum: currentNum, text: clean });
      } else if (clean.length > 0) {
        numbered.push({ lineNum: null, text: clean });
      }
    }

    const result = numbered
      .filter((l) => l.lineNum !== null && l.lineNum >= fromLine && l.lineNum <= toLine)
      .map((l) => l.text);

    return result.length > 0 ? result : null;
  }

  // Fallback: word-wrap and use sequential index
  const wrapped = wordWrapPassage(passageText, wrapWidth);
  const lines = wrapped.split("\n").filter((l) => l.trim().length > 0);
  if (toLine > lines.length) return null;
  return lines.slice(fromLine - 1, toLine);
}

// Parse "lines X-Y" or "lines X–Y" references from a question text.
// Returns { from, to } or null.
export function parseReReadLineRange(questionText) {
  if (!questionText) return null;
  const m = questionText.match(
    /re-?read\b[^.]*?lines?\s+(\d+)\s*[–—-]\s*(\d+)/i
  );
  if (m) return { from: parseInt(m[1], 10), to: parseInt(m[2], 10) };
  return null;
}

// Extract embedded line numbers from markdown passage text and convert to {L#} markers.
// The markdown from DataLab contains inline numbers like "...closing. 1  \n...houses...big 5  \n..."
// These are the exact line numbers from the exam paper. Convert them to {L1}, {L5} etc.
// so PassageWithLineNumbers can use exact line references instead of approximations.
export function convertMarkdownLineNumbersToMarkers(text) {
  if (!text) return text;
  // Match line numbers at end of a line: " 1  \n", " 5  \n", " 10  \n" etc.
  // The pattern is: space(s) + digits + spaces/newline
  // This handles markdown line numbers that appear at the end of sentences
  return text.replace(/\s+(\d+)\s*\n/g, (match, lineNum) => {
    // Only convert if it looks like a line number (1-200 range, typically)
    const num = parseInt(lineNum, 10);
    if (num >= 1 && num <= 300) {
      return ` {L${num}}\n`;
    }
    return match;
  });
}
